import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { format, isSameDay } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import {
  CalendarDays,
  CheckSquare,
  Loader2,
} from 'lucide-react';
import scheduleService from '../../services/scheduleService';

function DailySettleDialog({ open, onClose, dates, schedules, onConfirm, initialDate, singleDay = false }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (singleDay && initialDate) {
      return initialDate;
    }
    if (initialDate && dates.some(d => isSameDay(d, initialDate))) {
      return initialDate;
    }
    return dates[0] || new Date();
  });
  const [loading, setLoading] = useState(false);
  const [itemInputs, setItemInputs] = useState({});

  // Sync selectedDate when dialog opens with a new initialDate
  useEffect(() => {
    if (!open) return;
    if (singleDay && initialDate) {
      setSelectedDate(initialDate);
      setItemInputs({});
    } else if (initialDate && dates.some(d => isSameDay(d, initialDate))) {
      setSelectedDate(initialDate);
      setItemInputs({});
    } else if (dates.length > 0) {
      setSelectedDate(dates[0]);
      setItemInputs({});
    }
  }, [open, singleDay, initialDate, dates]);

  // When settling a historical date outside the current week, fetch its schedules
  const isHistoricalDate = singleDay && initialDate && !dates.some(d => isSameDay(d, initialDate));
  const [historicalSchedules, setHistoricalSchedules] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);

  useEffect(() => {
    if (!open || !isHistoricalDate || !initialDate) {
      setHistoricalSchedules([]);
      return;
    }

    let cancelled = false;
    setHistoricalLoading(true);
    const dateStr = format(initialDate, 'yyyy-MM-dd');
    scheduleService.getSchedules({ start_date: dateStr, end_date: dateStr })
      .then((data) => {
        if (!cancelled) {
          setHistoricalSchedules(data?.schedules || []);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch historical schedules:', err);
        if (!cancelled) setHistoricalSchedules([]);
      })
      .finally(() => {
        if (!cancelled) setHistoricalLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, isHistoricalDate, initialDate]);

  const effectiveSchedules = useMemo(() => {
    if (isHistoricalDate) {
      return historicalSchedules;
    }
    return schedules;
  }, [isHistoricalDate, historicalSchedules, schedules]);

  const settleData = useMemo(() => {
    const datesToRender = [...dates];
    // In single-day mode, ensure initialDate is included even if it's outside the current week
    if (singleDay && initialDate && !datesToRender.some(d => isSameDay(d, initialDate))) {
      datesToRender.push(initialDate);
    }

    const result = datesToRender.map((date) => {
      const daySchedules = effectiveSchedules.filter((s) => {
        const schDate = new Date(s.scheduled_date);
        return isSameDay(schDate, date);
      });
      const uncompleted = daySchedules.filter(
        (s) => !['completed', 'rolled_over', 'cancelled'].includes(s.status)
      );
      const completed = daySchedules.filter((s) => s.status === 'completed');
      return {
        date,
        total: daySchedules.length,
        uncompleted: uncompleted.length,
        completed: completed.length,
        uncompletedItems: uncompleted,
      };
    });
    return result;
  }, [dates, effectiveSchedules, singleDay, initialDate]);

  const selectedDay = settleData.find((d) => isSameDay(d.date, selectedDate));

  // Reset inputs when date changes
  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
    setItemInputs({});
  }, []);

  const handleInputChange = (scheduleId, field, value) => {
    setItemInputs((prev) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        [field]: value,
      },
    }));
  };

  const handleConfirm = async () => {
    if (!selectedDay) return;
    const items = selectedDay.uncompletedItems.map((item) => {
      const input = itemInputs[item._id] || {};
      return {
        schedule_id: item._id,
        completed_quantity: parseInt(input.completed_quantity || '0', 10) || 0,
        scrapped_quantity: parseInt(input.scrapped_quantity || '0', 10) || 0,
        notes: input.notes || '',
      };
    });

    setLoading(true);
    try {
      await onConfirm(selectedDate, items);
      setItemInputs({});
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            {singleDay && selectedDay
              ? format(selectedDay.date, 'yyyy年MM月dd日 结算', { locale: zhCN })
              : '日结算'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!singleDay && (
            <>
              <div className="text-sm text-gray-600">
                选择要结算的日期，填写每个任务的当日完成数量和报废数量。未完成的将自动加塞到次日。
              </div>

              {/* Date selector */}
              <div className="grid grid-cols-7 gap-1">
                {settleData.map((day) => (
                  <button
                    key={day.date.toISOString()}
                    onClick={() => handleSelectDate(day.date)}
                    className={`
                      p-2 rounded-md text-center text-xs transition-colors
                      ${isSameDay(day.date, selectedDate)
                        ? 'bg-blue-100 border border-blue-300 text-blue-800'
                        : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700'}
                    `}
                  >
                    <div className="font-semibold">{format(day.date, 'MM/dd')}</div>
                    <div className="text-[10px] mt-0.5">
                      {day.uncompleted > 0 ? (
                        <span className="text-amber-600">{day.uncompleted}待结</span>
                      ) : (
                        <span className="text-green-600">已结清</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {historicalLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              加载该日期排程数据...
            </div>
          ) : selectedDay ? (
            <div className="border rounded-md p-3 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">
                  {format(selectedDay.date, 'yyyy年MM月dd日', { locale: zhCN })}
                </span>
                <span className="text-xs text-gray-400">
                  共 {selectedDay.uncompletedItems.length} 个待结算任务
                </span>
              </div>

              {selectedDay.uncompleted > 0 ? (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  {selectedDay.uncompletedItems.map((item) => {
                    const input = itemInputs[item._id] || {};
                    const prevCompleted = item.completed_quantity || 0;
                    const inputCompleted = parseInt(input.completed_quantity || '0', 10) || 0;
                    // SCRAP does NOT count as done. Only completed reduces remaining.
                    const remaining = item.assigned_quantity - prevCompleted - inputCompleted;
                    const allDone = remaining <= 0;

                    return (
                      <div
                        key={item._id}
                        className={`text-xs bg-white rounded border p-2.5 ${allDone ? 'border-green-300 bg-green-50/30' : 'border-gray-200'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-gray-800">
                            {item.step_name}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {item.work_center_name} · {item.production_order_number}
                          </div>
                        </div>
                        <div className="text-[10px] text-gray-500 mb-2">
                          {item.finished_product_code} {item.finished_product_name && `· ${item.finished_product_name}`}
                          {' '}· 分配: <span className="font-medium text-gray-700">{item.assigned_quantity}</span>件
                          {item.completed_quantity > 0 && (
                            <span className="text-green-600 ml-1">
                              (之前已完成 {item.completed_quantity})
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[10px] text-gray-500">今日完成</Label>
                            <Input
                              type="number"
                              min="0"
                              className="h-7 text-xs mt-0.5"
                              value={input.completed_quantity || ''}
                              placeholder="0"
                              onChange={(e) => handleInputChange(item._id, 'completed_quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-500">今日报废</Label>
                            <Input
                              type="number"
                              min="0"
                              className="h-7 text-xs mt-0.5"
                              value={input.scrapped_quantity || ''}
                              placeholder="0"
                              onChange={(e) => handleInputChange(item._id, 'scrapped_quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-500">剩余</Label>
                            <div className={`h-7 flex items-center text-xs font-medium mt-0.5 px-2 rounded border ${allDone ? 'text-green-700 border-green-200 bg-green-50' : 'text-amber-700 border-amber-200 bg-amber-50'}`}>
                              {allDone ? '全部完成' : `${remaining} 件`}
                            </div>
                          </div>
                        </div>

                        <div className="mt-2">
                          <Input
                            className="h-7 text-xs"
                            placeholder="备注（可选）"
                            value={input.notes || ''}
                            onChange={(e) => handleInputChange(item._id, 'notes', e.target.value)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
                  当天所有任务已完成或已结算，无需操作。
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedDay || selectedDay.uncompleted === 0 || loading || historicalLoading}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            确认结算
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DailySettleDialog;
